import { useContext, useEffect, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ClockIcon,
  CrossIcon,
  SearchIcon,
  Settings04Icon,
} from 'assets/icons';
import { DropDownListHeader } from 'common/drop-down-list-header';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu } from 'common/menu';
import { VaultSearchBar } from 'common/vault-search-bar';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import { AlbumSortKey, albumSortOptions } from '../../single-album-utils';
import styles from './album-phone-header.module.scss';

// How far the page scrolls before the hero's title is under this bar, and
// the bar shows the album's name instead.
const TITLE_SCROLL = 64;

interface AlbumPhoneHeaderProps {
  title: string;
  onBack: () => void;
  sortBy: AlbumSortKey;
  onChangeSort: (sortBy: AlbumSortKey) => void;
  // The album has been arranged by hand, so "Custom order" is offered.
  showCustomOrder?: boolean;
}

// Phones: the mobile app's album header, in place of the vault top bar.
// Back; the album's name once the hero's title has scrolled away; search,
// which opens a search field over the bar; and sort.
export const AlbumPhoneHeader = ({
  title,
  onBack,
  sortBy,
  onChangeSort,
  showCustomOrder = false,
}: AlbumPhoneHeaderProps) => {
  const { dispatch } = useContext(VaultDetailContext);
  const [showTitle, setShowTitle] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  useClickOutside(sortRef, () => setShowSort(false));

  useEffect(() => {
    const onScroll = () => setShowTitle(window.scrollY > TITLE_SCROLL);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const search = (query: string) =>
    dispatch({
      type: vaultDetailActions.SET_SEARCH_QUERY,
      searchQuery: query,
    });

  const openSearch = () => {
    setSearchOpen(true);
    requestAnimationFrame(() =>
      searchRef.current?.querySelector('input')?.focus(),
    );
  };

  // Closing the search clears it, as in the app.
  const closeSearch = () => {
    setSearchOpen(false);
    search('');
  };

  return (
    <div className={styles.header}>
      <button
        type="button"
        className={styles.button}
        aria-label="Back"
        onClick={onBack}
      >
        <ArrowLeftIcon />
      </button>

      <span
        className={classnames(styles.title, {
          [styles.titleShown]: showTitle,
        })}
        aria-hidden={!showTitle}
      >
        {title}
      </span>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          aria-label="Search"
          onClick={openSearch}
        >
          <SearchIcon />
        </button>
        <div className={styles.sortWrap} ref={sortRef}>
          <button
            type="button"
            className={styles.button}
            aria-label="Sort"
            aria-expanded={showSort}
            onClick={() => setShowSort((prev) => !prev)}
          >
            <Settings04Icon />
          </button>
          {showSort && (
            <Menu position="" className={styles.sortMenu}>
              <DropDownListHeader title="Sort by:" />
              {albumSortOptions(showCustomOrder).map((option) => (
                <DropDownListItem
                  key={option.value}
                  leftIcon={<ClockIcon />}
                  className={classnames({
                    [styles.sortSelected]: option.value === sortBy,
                  })}
                  onClick={() => {
                    onChangeSort(option.value);
                    setShowSort(false);
                  }}
                >
                  {option.label}
                </DropDownListItem>
              ))}
            </Menu>
          )}
        </div>
      </div>

      {searchOpen && (
        <div className={styles.searchOverlay} ref={searchRef}>
          <VaultSearchBar placeholder="Search this album" onSearch={search} />
          <button
            type="button"
            className={styles.button}
            aria-label="Close search"
            onClick={closeSearch}
          >
            <CrossIcon />
          </button>
        </div>
      )}
    </div>
  );
};
